import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import {
  useDeleteEnrollment,
  useUpdateEnrollment,
} from "@/hooks/api/useEnrollments"
import type {
  EnrollmentResponse,
  EnrollmentUpdateInput,
} from "@/types/enrollment"
import { enrollmentUpdateSchema } from "@/types/enrollment"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { type Resolver, type SubmitHandler, useForm } from "react-hook-form"

interface EnrollmentActionsMenuProps {
  enrollment: EnrollmentResponse
}

export const EnrollmentActionsMenu = memo(
  ({ enrollment }: EnrollmentActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const deleteEnrollmentMutation = useDeleteEnrollment()
    const updateEnrollmentMutation = useUpdateEnrollment()

    const getDefaultValues = (): EnrollmentUpdateInput => ({
      student_id: enrollment.student_id ?? "",
      class_template_id: enrollment.class_template_id ?? "",
      session_id: enrollment.session_id ?? "",
      status: enrollment.status ?? null,
      notes: enrollment.notes ?? "",
    })

    const form = useForm<EnrollmentUpdateInput>({
      resolver: zodResolver(
        enrollmentUpdateSchema,
      ) as Resolver<EnrollmentUpdateInput>,
      mode: "onBlur",
      defaultValues: getDefaultValues(),
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(enrollment.student_id ?? enrollment.id)

    const handleDelete = async () => {
      try {
        await deleteEnrollmentMutation.mutateAsync(enrollment.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "Enrollment deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting enrollment:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete enrollment"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to delete this enrollment"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<EnrollmentUpdateInput> = async (data) => {
      try {
        await updateEnrollmentMutation.mutateAsync({
          enrollmentId: enrollment.id,
          enrollmentData: data,
        })
        setShowEditDialog(false)
        form.reset(getDefaultValues())
        toaster.create({
          title: "Enrollment updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating enrollment:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update enrollment"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this enrollment"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset(getDefaultValues())
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Enrollment</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.student_id}
                  errorText={getErrorText(form.formState.errors.student_id)}
                  label="Student Id"
                >
                  <Input
                    placeholder={"Enter student id"}
                    aria-invalid={!!form.formState.errors.student_id}
                    {...form.register("student_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.class_template_id}
                  errorText={getErrorText(
                    form.formState.errors.class_template_id,
                  )}
                  label="Class Template Id"
                >
                  <Input
                    placeholder={"Enter class template id"}
                    aria-invalid={!!form.formState.errors.class_template_id}
                    {...form.register("class_template_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.session_id}
                  errorText={getErrorText(form.formState.errors.session_id)}
                  label="Session Id"
                >
                  <Input
                    placeholder={"Enter session id"}
                    aria-invalid={!!form.formState.errors.session_id}
                    {...form.register("session_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.status}
                  errorText={getErrorText(form.formState.errors.status)}
                  label="Status"
                >
                  <select
                    {...form.register("status", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.status}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="">Select Status</option>{" "}
                    <option value="pending">Pending</option>{" "}
                    <option value="confirmed">Confirmed</option>{" "}
                    <option value="waitlisted">Waitlisted</option>{" "}
                    <option value="cancelled">Cancelled</option>{" "}
                    <option value="completed">Completed</option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateEnrollmentMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateEnrollmentMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Enrollment
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Enrollment</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The enrollment will be permanently
                removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteEnrollmentMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteEnrollmentMutation.isPending}
                >
                  {deleteEnrollmentMutation.isPending
                    ? "Deleting..."
                    : "Delete Enrollment"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

EnrollmentActionsMenu.displayName = "EnrollmentActionsMenu"

export default EnrollmentActionsMenu
